/**
 * Export the caption line as SVG, with per-glyph variation settings intact.
 *
 * Two decisions carry this file:
 *
 * **Positions are measured, not recomputed.** Every glyph is placed at the
 * coordinates the browser actually laid it out at. Re-flowing the text inside
 * SVG would mean reimplementing line breaking against advance widths that
 * change with `wdth` — and getting it subtly wrong, so the export would be a
 * picture of a line nobody ever saw. Reading `getBoundingClientRect` per glyph
 * makes wrapping, spacing and kerning exact by construction.
 *
 * **The font is embedded.** The spec asks for a file that opens correctly in a
 * browser. Referencing "Roboto Flex Variable" by name produces a file that
 * opens correctly on exactly one machine — the one that made it — and renders
 * in a fallback everywhere else, which is precisely where the variation
 * settings stop meaning anything. Roughly 440 kB of base64 is the price of the
 * export being true.
 *
 * The baseline is measured too, with a zero-height probe aligned to the
 * baseline, rather than derived from font metrics. Half-leading, line-height
 * and the ascender all conspire to make the arithmetic version wrong in a way
 * that looks almost right.
 */

import fontUrl from '@fontsource-variable/roboto-flex/files/roboto-flex-latin-full-normal.woff2?url';

const FONT_FAMILY = 'Roboto Flex Variable';

const escapeXml = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** The woff2, base64'd. Fetched once and kept — it does not change. */
let fontDataUri = null;

async function loadFontDataUri() {
  if (fontDataUri) return fontDataUri;
  const res = await fetch(fontUrl);
  if (!res.ok) throw new Error(`could not read the font for embedding (${res.status})`);
  const bytes = new Uint8Array(await res.arrayBuffer());

  // Chunked, because String.fromCharCode(...bytes) on a 326 kB array overflows
  // the argument limit and throws.
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  fontDataUri = `data:font/woff2;base64,${btoa(binary)}`;
  return fontDataUri;
}

/**
 * Measure every glyph, and the baseline of the line it sits on.
 *
 * Probes go in for all words at once and come out together, so the whole
 * measurement costs two layouts rather than two per word.
 *
 * @param {import('./caption.js').Caption} caption
 */
function measure(caption) {
  const words = caption.wordElements();

  const probes = words.map((wordEl) => {
    const probe = document.createElement('span');
    // Zero-sized and baseline-aligned: its top edge *is* the baseline.
    probe.style.cssText = 'display:inline-block;width:0;height:0;vertical-align:baseline';
    wordEl.append(probe);
    return probe;
  });

  const baselines = probes.map((p) => p.getBoundingClientRect().top);

  const glyphs = [];
  for (const [i, wordEl] of words.entries()) {
    for (const span of wordEl.querySelectorAll('.cap-char')) {
      const rect = span.getBoundingClientRect();
      if (!rect.width && !span.textContent.trim()) continue;
      const style = getComputedStyle(span);
      glyphs.push({
        ch: span.textContent,
        x: rect.left,
        baseline: baselines[i],
        fontSize: Number.parseFloat(style.fontSize),
        variation: style.fontVariationSettings,
        fill: style.color,
      });
    }
  }

  for (const p of probes) p.remove();
  return glyphs;
}

/**
 * @param {import('./caption.js').Caption} caption
 * @param {object} [options]
 * @param {boolean} [options.embedFont] default true
 * @param {string} [options.background] omit for a transparent export
 * @param {number} [options.padding]
 * @returns {Promise<string>} the SVG source
 */
export async function captionToSVG(caption, options = {}) {
  const { embedFont = true, background = '#0e0d10', padding = 24 } = options;

  const glyphs = measure(caption);
  if (!glyphs.length) throw new Error('nothing to export — the caption is empty');

  // Bounds from the glyphs themselves rather than the container, so an export
  // is cropped to the type instead of to whatever the panel happened to be.
  const left = Math.min(...glyphs.map((g) => g.x));
  const right = Math.max(...glyphs.map((g) => g.x + g.fontSize * 1.2));
  const top = Math.min(...glyphs.map((g) => g.baseline - g.fontSize));
  const bottom = Math.max(...glyphs.map((g) => g.baseline + g.fontSize * 0.3));

  const width = Math.ceil(right - left + padding * 2);
  const height = Math.ceil(bottom - top + padding * 2);
  const ox = padding - left;
  const oy = padding - top;

  const parts = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" ` +
      `viewBox="0 0 ${width} ${height}">`,
  );

  if (embedFont) {
    const uri = await loadFontDataUri();
    parts.push(
      '<style>@font-face{' +
        `font-family:'${FONT_FAMILY}';` +
        `src:url(${uri}) format('woff2-variations');` +
        'font-weight:100 1000;font-stretch:25% 151%;' +
        '}</style>',
    );
  }

  if (background) {
    parts.push(`<rect width="${width}" height="${height}" fill="${escapeXml(background)}"/>`);
  }

  for (const g of glyphs) {
    if (!g.ch.trim()) continue;
    const x = (g.x + ox).toFixed(2);
    const y = (g.baseline + oy).toFixed(2);
    parts.push(
      `<text x="${x}" y="${y}" font-family="${FONT_FAMILY}" font-size="${g.fontSize.toFixed(2)}" ` +
        `fill="${escapeXml(g.fill)}" ` +
        `style="font-variation-settings:${escapeXml(g.variation)}">${escapeXml(g.ch)}</text>`,
    );
  }

  parts.push('</svg>');
  return parts.join('\n');
}

/** Export and hand the file to the browser. */
export async function downloadCaptionSVG(caption, filename, options) {
  const svg = await captionToSVG(caption, options);
  const blob = new Blob([svg], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? `visible-speech-${new Date().toISOString().replace(/[:.]/g, '-')}.svg`;
  a.click();
  URL.revokeObjectURL(url);
  return svg.length;
}

export default captionToSVG;
